import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link ,useLocation, useNavigate} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sagaActions } from '../../../sagas/sagaActions';
import { getUserSelector } from '../../../redux/reducers/reducerSlices/UserReducer';
import _ from 'lodash';
import CustomDropDown from '../Common/CustomDropDown/CustomDropDown';
import './Navigationbar.css';
import { nav } from 'aws-amplify';
import FloatingChat from './FloatingChat';
import { removeInterceptor } from '../../../redux/api/Interceptor';
import ReactDOM from 'react-dom';

/**
 * This component render PRIMARY navigation bar
 * @param {*} props 
 * @returns 
 */
const Navigationbar = (props) => {

    const { themeSelected } = props;
    const userPermission = useSelector(getUserSelector);
    const dispatch = useDispatch()
    const { navigationBarList } = userPermission;
    const location = useLocation();
    const navigate = useNavigate()

    const navListRef = useRef(null);
    const reportsNavRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft]   = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [showReportsMenu, setShowReportsMenu] = useState(false);
    const [reportsMenuPos, setReportsMenuPos] = useState({ top: 0, left: 0 });

    const checkScroll = useCallback(() => {
        const el = navListRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 2);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    }, []);

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [checkScroll, navigationBarList]);

    /** Scroll the active nav item into the centre of the visible area
     *  whenever the route changes (covers the "last tab selected" case). */
    useEffect(() => {
        const el = navListRef.current;
        if (!el) return;
        // Small delay so the DOM has finished painting the active class
        const raf = requestAnimationFrame(() => {
            const activeItem = el.querySelector('.d-nav-active');
            if (activeItem) {
                const itemLeft   = activeItem.offsetLeft;
                const itemWidth  = activeItem.offsetWidth;
                const containerW = el.clientWidth;
                // Centre the active item in the viewport of the nav list
                const target = itemLeft - containerW / 2 + itemWidth / 2;
                el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
            }
            checkScroll();
        });
        return () => cancelAnimationFrame(raf);
    }, [location.pathname, checkScroll]);

    const scrollNav = (dir) => {
        const el = navListRef.current;
        if (!el) return;
        el.scrollBy({ left: dir === 'right' ? 220 : -220, behavior: 'smooth' });
    };

    /** Close reports menu when clicking outside */
    useEffect(() => {
        if (!showReportsMenu) return;
        const handler = (e) => {
            if (
                reportsNavRef.current && !reportsNavRef.current.contains(e.target) &&
                !document.getElementById('reports-portal-menu')?.contains(e.target)
            ) {
                setShowReportsMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showReportsMenu]);

    /**useEffect for navigation bar list data */
    useEffect(() => {
        if (navigationBarList.length) {
            renderNavBar()
        } else {
            // If navigationBarList is empty for any reason API fail or naother,w e should not let user to go inside component and log him out
            removeInterceptor()
            dispatch({ type: sagaActions.CLEAR_SIGNEDIN_USER })
            dispatch({ type: sagaActions.RESET_USER })
            navigate("/")
            window.localStorage.clear();
        }
    }, [navigationBarList])

    /** Click Handler function for Navigation bar */
    const clickHandler = (el) => {
        const { PageUrl, PageDesc } = el;
        if (PageDesc === 'Home' && PageUrl === '/warehousemanagement') {
            dispatch({ type: sagaActions.UPDATE_SELECTED_WAREHOUSE, payload: {} })
        }
        else if (PageDesc === 'Home' && PageUrl === '/') {
            dispatch({ type: sagaActions.RESET_USER_PERMISSIONS })
            dispatch({ type: sagaActions.PARENT_WAREHOUSE, payload: {} })
        }
    }

    /** Handler function for  report selection from dropdown*/
    const onReportSelected = (event, obj, index) => {
        dispatch({ type: sagaActions.UPDATE_SELECTED_REPORT, payload: event })
        setShowReportsMenu(false);
    }

    /** Toggle Reports portal menu and compute fixed position */
    const handleReportsClick = () => {
        if (reportsNavRef.current) {
            const rect = reportsNavRef.current.getBoundingClientRect();
            setReportsMenuPos({
                top: rect.bottom + 4,
                left: rect.left,
            });
        }
        setShowReportsMenu(prev => !prev);
    };

    /** function to return accessible reports array */
    const getAccessibleReports = (dataArr) => {
        return dataArr.filter((reportObj) => {
            return reportObj.Permission &&
                reportObj.ElementAccess !== 'Receipt Retrieval Filter' &&
                reportObj.ElementAccess !== 'WarehouseDropdown';
        });
    };

    /** function to return JSX for reports dropdown in navigation bar */
    const getReportsArrayDropDown = (obj, index, dataArr, placeHolder, onItemSelected) => {
        const accessibleReportsArr = dataArr.filter((reportObj, index) => {
            if(reportObj.Permission && (reportObj.ElementAccess !== 'Receipt Retrieval Filter' && reportObj.ElementAccess !== "WarehouseDropdown")){
                return reportObj
            }
        })
        return <CustomDropDown
            themeSelected={'light-mode'}
            data={accessibleReportsArr}
            titleStyle={'dropDownNavBarTitle'}
            placeHolder={placeHolder}
            onSelect={(event) => onItemSelected(event, obj, index)}
            keyStr={'ElementAccess'}
            hideSelectedItem={true} />
    }

    /**
     * 
     * @returns JSX for PRIMARY navigation
     */
    const renderNavBar = () => {
        let navBarArr = navigationBarList.map((el, index) => {
            if(el.PageName === 'Live Chat'){
                return ;
            }
            if (el.NavigationType === 'Primary' && el.PageAccess) {
                if (el.PageName === "Reports") {
                    const accessibleReports = getAccessibleReports(el.featureRole || []);
                    return (
                        <li
                            key={el.PageDesc}
                            className={location.pathname === el.PageUrl ? "d-nav nav-item mainNavigationBar d-nav-active" : "d-nav nav-item mainNavigationBar"}
                            style={{ padding: '0 6px' }}
                            ref={reportsNavRef}
                        >
                            <div
                                className="nav-link text-center py-1 px-0"
                                style={{ cursor: 'pointer' }}
                                onClick={handleReportsClick}
                            >
                                <div className="dropdown rounded py-1 px-2 customDropdown">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className={`dropDownPlaceholder light-mode me-1 dropDownNavBarTitle`}>
                                            {el.PageDesc}
                                        </span>
                                        <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                    </div>
                                </div>
                            </div>
                            {showReportsMenu && ReactDOM.createPortal(
                                <ul
                                    id="reports-portal-menu"
                                    className="dropdown-menu show"
                                    style={{
                                        position: 'fixed',
                                        top: reportsMenuPos.top,
                                        left: reportsMenuPos.left,
                                        zIndex: 99999,
                                        maxHeight: '50vh',
                                        overflowY: 'auto',
                                        minWidth: '180px',
                                    }}
                                >
                                    {accessibleReports.map((report, i) => (
                                        <li key={i}>
                                            <a
                                                className="dropdown-item"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    navigate(el.PageUrl);
                                                    onReportSelected(report, el, index);
                                                }}
                                            >
                                                {report.ElementAccess}
                                            </a>
                                        </li>
                                    ))}
                                </ul>,
                                document.body
                            )}
                        </li>
                    );
                }
                return (<li className={location.pathname===el.PageUrl?"d-nav nav-item mainNavigationBar d-nav-active":"d-nav nav-item mainNavigationBar"} key={el.PageDesc} style={{padding: '0 6px'}}>
                    <Link className="nav-link text-center py-1 px-0" aria-current="page" to={el.PageUrl} onClick={() => clickHandler(el)}>
                        <div className='navText'>{el.PageDesc}</div>
                    </Link>
                </li>)
            }
        })
        return _.compact(navBarArr)
    }

    return (
      <div className="setBorderTop setBorderBottom">
        <nav className={`d-navbar navbar navbar-expand ${themeSelected}`}>
          <div className="container-fluid px-0">
            <div className="nav-scroll-wrapper navbar-expand w-100" id="navbarNav">
              {canScrollLeft && (
                <button
                  className={`nav-scroll-btn nav-scroll-btn--left ${themeSelected}`}
                  onClick={() => scrollNav('left')}
                  aria-label="Scroll navigation left"
                >
                  <span className="nav-scroll-icon">
                    <i className="fa-solid fa-chevron-left" />
                  </span>
                </button>
              )}
              <ul
                className="navbar-nav d-flex flex-row"
                ref={navListRef}
                onScroll={checkScroll}
              >
                {navigationBarList.length ? renderNavBar() : ""}
              </ul>
              {canScrollRight && (
                <button
                  className={`nav-scroll-btn nav-scroll-btn--right ${themeSelected}`}
                  onClick={() => scrollNav('right')}
                  aria-label="Scroll navigation right"
                >
                  <span className="nav-scroll-icon">
                    <i className="fa-solid fa-chevron-right" />
                  </span>
                </button>
              )}
            </div>
          </div>
        </nav>
      </div>
    );
}

export default Navigationbar;
